'use client';

import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '../utils';

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    // `bg-scrim`, NOT `bg-scrim`: a backdrop must darken in every theme.
    // `sink` resolves to white in light, which lightens the page and
    // leaves the dialog with no separation. See `scrim` in tailwind.config.
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-scrim/50 fixed inset-0 z-[90] backdrop-blur-sm',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  modal = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /**
   * Extra classes for the auto-mounted backdrop overlay. Mainly used to raise
   * the dialog's stacking context above another overlay — e.g. above the
   * floating echo bot at `z-[9999]`, or stacking the image cropper on top of
   * the bio's My Account modal. `cn` de-dupes the z-class so callers can pass
   * a higher `z-` (e.g. `z-[200]`) to override the default `z-[90]`; pass a
   * matching higher `z-` on `className` too so both backdrop and content clear
   * the lower layer.
   */
  overlayClassName?: string;
  /**
   * Set `false` for a STACKABLE dialog — one another intercept (checkout /
   * subscribe) can open ON TOP of. A modal Radix dialog's `react-remove-scroll`
   * sets `body { pointer-events: none }`, which the overlay stacked above
   * inherits, rendering it inert. Non-modal drops that guard; the backdrop still
   * renders (a plain div, since Radix's `DialogOverlay` returns `null` when the
   * Root is non-modal). The CALLER must ALSO pass `<Dialog modal={false}>` and
   * typically `onInteractOutside={e => e.preventDefault()}` so interacting with
   * the modal on top doesn't dismiss this one. Defaults to `true` (unchanged).
   */
  modal?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      {modal ? (
        <DialogOverlay className={overlayClassName} />
      ) : (
        <div
          data-slot="dialog-overlay"
          aria-hidden
          className={cn(
            'animate-in fade-in-0 bg-scrim/50 fixed inset-0 z-[90] backdrop-blur-sm',
            overlayClassName,
          )}
        />
      )}
      <DialogPrimitive.Content
        aria-describedby={undefined}
        data-slot="dialog-content"
        className={cn(
          'bg-card text-card-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 border-border fixed left-[50%] top-[50%] z-[90] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none [&_svg:not([class*='size-'])]:h-4 [&_svg:not([class*='size-'])]:w-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg font-semibold leading-none', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      // RULE: a popup's DESCRIPTION is always DM Sans (`font-sans`), never the
      // display face. A dialog is often opened from a surface that sets
      // `font-display` — the whole `(dashboard)` tree does — and a description
      // is running prose, which the heading face makes heavy and slow to read
      // at body size. Stated on the ATOM so every popup inherits it rather than
      // each one remembering; a caller's own `className` still wins.
      className={cn('text-muted-foreground font-sans text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};

/**
 * Re-export of the raw radix primitive namespace for callers that need
 * to build a custom-styled surface (e.g. a side-anchored drawer that
 * the opinionated centered `DialogContent` can't express). Prefer the
 * wrapped exports above for ordinary modal dialogs.
 */
export { DialogPrimitive };

/**
 * Shared sizing rules for bio-flow modals.
 *
 * Two goals:
 *   1. Stable height on desktop — the modal locks to a
 *      viewport-derived size so its outer box doesn't reflow
 *      while inner content streams in (no jump between
 *      "Loading…" and the real content).
 *   2. Full-screen on mobile — the modal occupies the whole
 *      device viewport.
 *
 * Tailwind constraint: all class names below are written as
 * literal strings so the JIT compiler picks them up. Do NOT
 * build these via string concatenation — `sm:h-[${var}]` at
 * runtime won't be in the generated CSS.
 */

/**
 * Default bio modal — 480px wide (matches `BioShell.tsx:49`'s
 * `max-w-[480px]` bio column), locked to 85vh on desktop,
 * full-screen on mobile. Outer box never reflows during
 * loading transitions.
 */
export const BIO_MODAL_CONTENT =
  'flex flex-col gap-0 p-0 overflow-hidden ' +
  'sm:w-[480px] sm:max-w-[calc(100vw-32px)] sm:h-[85vh] sm:rounded-2xl ' +
  // Mobile height: `h-dvh` not `h-screen`/`100vh` — iOS Safari's `100vh` counts
  // the area behind the browser chrome, clipping top headers + bottom CTAs.
  'max-sm:!w-screen max-sm:h-dvh max-sm:!max-w-none max-sm:rounded-none max-sm:border-0';

/**
 * Compact bio modal — same mobile rules, but desktop sizes
 * to content (capped at 85vh) instead of locking. Use for
 * short form-style modals where 85vh would waste screen.
 */
export const BIO_MODAL_CONTENT_COMPACT =
  'flex flex-col gap-0 p-0 overflow-hidden ' +
  'sm:max-w-[400px] sm:max-h-[85vh] sm:rounded-2xl ' +
  // Mobile height: `h-dvh` not `h-screen`/`100vh` — see `BIO_MODAL_CONTENT`.
  'max-sm:!w-screen max-sm:h-dvh max-sm:!max-w-none max-sm:rounded-none max-sm:border-0';

/**
 * Wide bio modal — for the video player which sits on top of
 * a comments side panel and needs a wider footprint than the
 * 480px bio column. Sized at 60vh (~70% of the legacy 85vh)
 * so the 9:16 video column reads as a portrait phone preview
 * rather than dominating the viewport. The comments side
 * panel's width formula uses the same cap (see VideoModal /
 * SeriesVideoPlayer).
 */
export const BIO_MODAL_CONTENT_WIDE =
  'flex flex-col gap-0 p-0 overflow-hidden ' +
  'sm:!w-fit sm:!max-w-[90vw] sm:h-[60vh] sm:rounded-2xl ' +
  // Mobile: `h-dvh` (dynamic viewport height), NOT `h-screen`/`100vh`. On iOS
  // Safari `100vh` is the LARGE viewport — it counts the area behind the
  // address bar + bottom toolbar, so a `100vh` modal is taller than the
  // visible viewport and its `object-cover` video + product footer got clipped
  // top & bottom behind the browser chrome (QA: iOS player cut off). `100dvh`
  // tracks the currently-visible height as the chrome shows/hides.
  'max-sm:!w-screen max-sm:h-dvh max-sm:!max-w-none max-sm:rounded-none max-sm:border-0';

/**
 * Inner scroll container — pair with any of the BIO_MODAL_*
 * content classes. The `flex-1 min-h-0` combo is load-bearing:
 *   - `flex-1` lets this fill the dialog's remaining height
 *   - `min-h-0` defeats flex's "don't shrink below content"
 *     default, without which internal scroll never engages
 */
export const BIO_MODAL_SCROLL =
  'flex-1 min-h-0 overflow-y-auto overscroll-contain';
